import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Book, 
  Bell as BellIcon, 
  Plus, 
  ChevronLeft, 
  Edit, 
  Trash2, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  Clipboard,
  Mail,
  Loader,
  Search,
  Library,
  PlusCircle,
  RefreshCw,
  X,
  ExternalLink
} from 'lucide-react';
import LibraryBuilderModal from './LibraryBuilder';
import LibraryImportModal from './LibraryImport';
import surveyService from '../../services/surveyService';
import reminderService from '../../services/reminderService';
import toast from 'react-hot-toast';

// Helper function to parse markdown
const parseMarkdown = (text) => {
  if (!text) return '';
  
  // Convert basic markdown to HTML
  // Convert **bold** to <strong>bold</strong>
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em>italic</em>
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert URLs to links
  html = html.replace(
    /(https?:\/\/[^\s]+)/g, 
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:underline">$1</a>'
  );
  
  // Convert line breaks to <br />
  html = html.replace(/\n/g, '<br />');
  
  return html;
};

const GlobalLibraryDashboard = () => {
  const navigate = useNavigate();

  // State for library items
  const [surveys, setSurveys] = useState([]);
  const [missingForms, setMissingForms] = useState([]);
  const [emailReminders, setEmailReminders] = useState([]);
  const [pushReminders, setPushReminders] = useState([]);
  const [forceDeleteMode, setForceDeleteMode] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  
  // State for loading indicators
  const [loading, setLoading] = useState({
    surveys: false,
    missingForms: false,
    emailReminders: false,
    pushReminders: false
  });
  
  // State for error messages
  const [error, setError] = useState({
    surveys: null,
    missingForms: null,
    emailReminders: null,
    pushReminders: null
  });
  
  // State for expanded items
  const [expandedStandardSurvey, setExpandedStandardSurvey] = useState(null);
  const [expandedMissingForm, setExpandedMissingForm] = useState(null);
  const [expandedReminder, setExpandedReminder] = useState(null);
  
  // State for modal management
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  
  // State for delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [deleteReminderModalOpen, setDeleteReminderModalOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);

  // Fetch library items on component mount
  useEffect(() => {
    fetchGlobalLibraryItems();
  }, []);

  // Function to fetch all global library items
  const fetchGlobalLibraryItems = async () => {
    fetchGlobalSurveys();
    fetchGlobalMissingForms();
    fetchGlobalEmailReminders();
    fetchGlobalPushReminders();
  };

  // Function to fetch standard surveys from the global library
  const fetchGlobalSurveys = async () => {
    setLoading(prev => ({ ...prev, surveys: true }));
    setError(prev => ({ ...prev, surveys: null }));
    
    try {
      const response = await surveyService.getGlobalSurveys('standard');
      setSurveys(response.data);
    } catch (err) {
      console.error('Error fetching global surveys:', err);
      setError(prev => ({ 
        ...prev, 
        surveys: 'Failed to fetch global surveys. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, surveys: false }));
    }
  };

  // Function to fetch missing forms from the global library
  const fetchGlobalMissingForms = async () => {
    setLoading(prev => ({ ...prev, missingForms: true }));
    setError(prev => ({ ...prev, missingForms: null }));
    
    try {
      const response = await surveyService.getGlobalSurveys('missing_form');
      setMissingForms(response.data);
    } catch (err) {
      console.error('Error fetching global missing forms:', err);
      setError(prev => ({ 
        ...prev, 
        missingForms: 'Failed to fetch global missing forms. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, missingForms: false }));
    }
  };

  // Function to fetch email reminders from the global library
  const fetchGlobalEmailReminders = async () => {
    setLoading(prev => ({ ...prev, emailReminders: true }));
    setError(prev => ({ ...prev, emailReminders: null }));
    
    try {
      const response = await reminderService.getGlobalReminders('email');
      setEmailReminders(response.data);
    } catch (err) {
      console.error('Error fetching global email reminders:', err);
      setError(prev => ({ 
        ...prev, 
        emailReminders: 'Failed to fetch global email reminders. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, emailReminders: false }));
    }
  };

  // Function to fetch push reminders from the global library
  const fetchGlobalPushReminders = async () => {
    setLoading(prev => ({ ...prev, pushReminders: true }));
    setError(prev => ({ ...prev, pushReminders: null }));
    
    try {
      const response = await reminderService.getGlobalReminders('push');
      setPushReminders(response.data);
    } catch (err) {
      console.error('Error fetching global push reminders:', err);
      setError(prev => ({ 
        ...prev, 
        pushReminders: 'Failed to fetch global push reminders. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, pushReminders: false }));
    }
  };

  // Function to handle creating a new library item
  const handleCreateNew = (type) => {
    // Map the button types to the expected LibraryBuilderModal types
    const modalTypeMap = {
      'standard': 'standard_survey',
      'missing_form': 'missing_form',
      'email': 'email_reminder',
      'push_notification': 'push_notification'
    };
    
    setSelectedType(modalTypeMap[type] || type);
    setEditingSurvey(null);
    setEditingReminder(null);
    setModalOpen(true);
  };

  // Function to handle editing a survey
  const handleEditSurvey = (survey) => {
    const type = survey.survey_type === 'missing_form' ? 'missing_form' : 'standard_survey';
    setSelectedType(type);
    setEditingSurvey(survey);
    setEditingReminder(null);
    setModalOpen(true);
  };

  // Function to handle editing a reminder
  const handleEditReminder = (reminder) => {
    // Map the channel to the correct type expected by the LibraryBuilderModal
    const type = reminder.channel === 'push' ? 'push_notification' : 'email_reminder';
    setSelectedType(type);
    setEditingReminder(reminder);
    setEditingSurvey(null);
    setModalOpen(true);
  };

  // Function to handle deleting a survey
  const handleDeleteSurvey = async (surveyId) => {
    try {
      await surveyService.deleteGlobalSurvey(surveyId, forceDeleteMode);
      
      // Refresh surveys after deletion
      if (surveyToDelete?.survey_type === 'missing_form') {
        fetchGlobalMissingForms();
      } else {
        fetchGlobalSurveys();
      }
      
      // Close the modal and reset states
      setDeleteModalOpen(false);
      setSurveyToDelete(null);
      setForceDeleteMode(false);
      setDeleteErrorMessage('');
      
      // Show success toast
      toast.success(`${surveyToDelete?.survey_type === 'missing_form' ? 'Missing form' : 'Survey'} deleted successfully`);
    } catch (err) {
      console.error('Error deleting survey:', err);
      
      // Check if the error has a response with an error message
      if (err.response && err.response.data && err.response.data.error) {
        const errorMsg = err.response.data.error;
        setDeleteErrorMessage(errorMsg);
        
        // Don't close the modal if there's an error and it's about associations
        if (errorMsg.includes("associated with") || errorMsg.includes("completed response")) {
          // Keep the modal open to show force delete option
          return;
        }
        
        toast.error(errorMsg);
      } else {
        toast.error('Failed to delete. Please try again.');
      }
      
      // Close the modal and reset states for other error types
      setDeleteModalOpen(false);
      setSurveyToDelete(null);
      setForceDeleteMode(false);
      setDeleteErrorMessage('');
    }
  };

  // Function to handle deleting a reminder
  const handleDeleteReminder = async (reminderId) => {
    try {
      await reminderService.deleteGlobalReminder(reminderId);
      
      // Refresh reminders after deletion
      if (reminderToDelete?.channel === 'push') {
        fetchGlobalPushReminders();
      } else {
        fetchGlobalEmailReminders();
      }
      
      // Close the modal
      setDeleteReminderModalOpen(false);
      setReminderToDelete(null);
      
      // Show success toast
      toast.success(`${reminderToDelete?.channel === 'push' ? 'Push notification' : 'Email reminder'} deleted successfully`);
    } catch (err) {
      console.error('Error deleting reminder:', err);
      if (err.response && err.response.data && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Failed to delete. Please try again.');
      }
    }
  };

  // Function to handle callback after library item is created or updated
  const handleLibraryUpdated = (type) => {
    console.log('Library updated with type:', type);
    // Refresh all data to ensure everything is up-to-date
    fetchGlobalSurveys();
    fetchGlobalMissingForms();
    fetchGlobalEmailReminders();
    fetchGlobalPushReminders();
    
    // Close modal
    setModalOpen(false);
    setEditingSurvey(null);
    setEditingReminder(null);
    setSelectedType(null);
  };

  // Component for section headers
  const SectionHeader = ({ icon: Icon, title, count, isLoading }) => (
    <div className="flex items-center justify-between mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
          <Icon size={20} />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {!isLoading && count !== undefined && (
          <span className="ml-2 px-2.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-full">
            {count}
          </span>
        )}
      </div>
    </div>
  );

  // Component for loading states
  const LoadingState = () => (
    <div className="flex items-center justify-center py-8">
      <Loader size={24} className="text-primary-600 animate-spin mr-3" />
      <span className="text-gray-500 dark:text-gray-400">Loading...</span>
    </div>
  );

  // Component for error states
  const ErrorState = ({ message, onRetry }) => (
    <div className="bg-red-50 rounded-xl p-4 flex items-start">
      <AlertTriangle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-red-600 font-medium">{message}</p>
        {onRetry && (
          <button 
            onClick={onRetry}
            className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium flex items-center"
          >
            <RefreshCw size={14} className="mr-1" /> Try again
          </button>
        )}
      </div>
    </div>
  );

  // Component for empty states
  const EmptyState = ({ type, onAdd }) => (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-8 text-center">
      <div className="w-12 h-12 mx-auto bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 rounded-full flex items-center justify-center mb-4">
        {type === 'standard' && <Book size={24} />}
        {type === 'missing_form' && <Clipboard size={24} />}
        {type === 'email' && <Mail size={24} />}
        {type === 'push' && <BellIcon size={24} />}
      </div>
      <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-2">
        No {type === 'standard' ? 'standard surveys' : 
             type === 'missing_form' ? 'missing forms' : 
             type === 'email' ? 'email reminders' : 
             'push notifications'} found
      </h3>
      <p className="text-gray-500 dark:text-gray-400 mb-4">
        Create your first {type === 'standard' ? 'standard survey' : 
                          type === 'missing_form' ? 'missing form' : 
                          type === 'email' ? 'email reminder' : 
                          'push notification'} to get started.
      </p>
      <button
        onClick={onAdd}
        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm dark:shadow-gray-900/20"
      >
        <Plus size={16} className="mr-2" />
        Add {type === 'standard' ? 'Standard Survey' : 
              type === 'missing_form' ? 'Missing Form' : 
              type === 'email' ? 'Email Reminder' : 
              'Push Notification'}
      </button>
    </div>
  );

  // Component for library item cards
  const LibraryItemCard = ({ 
    item, 
    type, 
    isExpanded, 
    onToggle, 
    onEdit, 
    onDelete 
  }) => {
    const isReminder = type === 'email' || type === 'push';
    
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 overflow-hidden transition-all duration-200 hover:shadow-md dark:hover:shadow-gray-900/20 bg-white dark:bg-gray-800"
      >
        <div 
          className="flex items-center justify-between p-4 cursor-pointer group"
          onClick={onToggle}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-primary-50 text-primary-600 transition-colors duration-200">
              {type === 'standard' && <Book size={18} />}
              {type === 'missing_form' && <Clipboard size={18} />}
              {type === 'email' && <Mail size={18} />}
              {type === 'push' && <BellIcon size={18} />}
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors duration-200">
                {item.name}
              </h3>
              {type === 'email' && item.metadata?.subject && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Subject: {item.metadata.subject}</p>
              )}
              {!isReminder && item.short_description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{item.short_description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors duration-200"
              aria-label="Edit"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors duration-200"
              aria-label="Delete"
            >
              <Trash2 size={16} />
            </button>
            <div className="p-2 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200">
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </div>
        </div>
        
        {isExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
            >
              <div className="p-4">
                {/* Survey/Form specific details */}
                {!isReminder && (
                  <>
                    {item.long_description && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Description:</h4>
                        <div 
                          className="text-sm text-gray-600 dark:text-gray-300 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: parseMarkdown(item.long_description) 
                          }}
                        />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Status:</h4>
                        <div className="text-sm">
                          <span 
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              item.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Questions:</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 px-2.5 py-1 rounded-full border border-gray-100 dark:border-gray-700 inline-block">
                          {item.questions?.length || 0} questions
                        </p>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Reminder specific details */}
                {isReminder && (
                  <>
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Template Content:</h4>
                      <div className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 overflow-auto max-h-48 whitespace-pre-wrap">
                        {item.template_content}
                      </div>
                    </div>
                    
                    {item.placeholders && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Available Placeholders:</h4>
                        <div className="flex flex-wrap gap-2">
                          {item.placeholders.required?.map((placeholder) => (
                            <span 
                              key={placeholder}
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              ${placeholder}
                            </span>
                          ))}
                          {item.placeholders.optional?.map((placeholder) => (
                            <span 
                              key={placeholder}
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                            >
                              ${placeholder} (optional)
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Version:</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 px-2.5 py-1 rounded-full border border-gray-100 dark:border-gray-700 inline-block">
                        {item.version || '1.0.0'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
      </div>
    );
  };
  
  // Action buttons for creating new items
  const ActionButton = ({ icon: Icon, label, onClick, variant = 'primary' }) => (
    <button
      onClick={onClick}
      className={`flex items-center px-4 py-2 rounded-lg shadow-sm dark:shadow-gray-900/20 transition-all duration-200 hover:shadow dark:hover:shadow-gray-900/20 ${
        variant === 'primary' ? 
          'bg-primary-600 text-white hover:bg-primary-700' : 
          'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      <Icon size={16} className="mr-2" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <button
              onClick={() => navigate('/studybuilder')}
              className="group flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 mr-1 group-hover:transform group-hover:-translate-x-1 transition-transform duration-200" />
              <span>Back to Study Builder</span>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
                <Library size={24} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Global Library</h1>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => fetchGlobalLibraryItems()}
                className="flex items-center text-primary-600 hover:text-primary-700 transition-colors"
              >
                <RefreshCw size={16} className="mr-1" />
                <span>Refresh Library</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden">
          {/* Introduction and quick actions */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Manage Global Library Items</h2>
              <p className="text-gray-600 dark:text-gray-300">
                The global library contains reusable items that can be imported into any study. 
                Create, edit, and manage global surveys and reminders that can be shared across all your studies.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ActionButton 
                icon={Book} 
                label="New Standard Survey" 
                onClick={() => handleCreateNew('standard')} 
              />
              <ActionButton 
                icon={Clipboard} 
                label="New Missing Form" 
                onClick={() => handleCreateNew('missing_form')} 
              />
              <ActionButton 
                icon={Mail} 
                label="New Email Reminder" 
                onClick={() => handleCreateNew('email')} 
              />
              <ActionButton 
                icon={BellIcon} 
                label="New Push Reminder" 
                onClick={() => handleCreateNew('push_notification')} 
              />
            </div>
          </div>

          {/* Library content sections */}
          <div className="p-6">
            {/* Standard Surveys */}
            <section className="mb-10">
              <SectionHeader 
                icon={Book} 
                title="Standard Surveys" 
                count={surveys.length} 
                isLoading={loading.surveys} 
              />
              
              {loading.surveys ? (
                <LoadingState />
              ) : error.surveys ? (
                <ErrorState 
                  message={error.surveys} 
                  onRetry={fetchGlobalSurveys} 
                />
              ) : surveys.length === 0 ? (
                <EmptyState 
                  type="standard" 
                  onAdd={() => handleCreateNew('standard')} 
                />
              ) : (
                <div className="space-y-4">
                  {surveys.map((survey) => (
                    <LibraryItemCard
                      key={survey.id}
                      item={survey}
                      type="standard"
                      isExpanded={expandedStandardSurvey === survey.id}
                      onToggle={() => setExpandedStandardSurvey(
                        expandedStandardSurvey === survey.id ? null : survey.id
                      )}
                      onEdit={() => handleEditSurvey(survey)}
                      onDelete={() => {
                        setSurveyToDelete(survey);
                        setDeleteModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </section>
            
            {/* Missing Forms */}
            <section className="mb-10">
              <SectionHeader 
                icon={Clipboard} 
                title="Missing Forms" 
                count={missingForms.length} 
                isLoading={loading.missingForms} 
              />
              
              {loading.missingForms ? (
                <LoadingState />
              ) : error.missingForms ? (
                <ErrorState 
                  message={error.missingForms} 
                  onRetry={fetchGlobalMissingForms} 
                />
              ) : missingForms.length === 0 ? (
                <EmptyState 
                  type="missing_form" 
                  onAdd={() => handleCreateNew('missing_form')} 
                />
              ) : (
                <div className="space-y-4">
                  {missingForms.map((form) => (
                    <LibraryItemCard
                      key={form.id}
                      item={form}
                      type="missing_form"
                      isExpanded={expandedMissingForm === form.id}
                      onToggle={() => setExpandedMissingForm(
                        expandedMissingForm === form.id ? null : form.id
                      )}
                      onEdit={() => handleEditSurvey(form)}
                      onDelete={() => {
                        setSurveyToDelete(form);
                        setDeleteModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </section>
            
            {/* Email Reminders */}
            <section className="mb-10">
              <SectionHeader 
                icon={Mail} 
                title="Email Reminders" 
                count={emailReminders.length} 
                isLoading={loading.emailReminders} 
              />
              
              {loading.emailReminders ? (
                <LoadingState />
              ) : error.emailReminders ? (
                <ErrorState 
                  message={error.emailReminders} 
                  onRetry={fetchGlobalEmailReminders} 
                />
              ) : emailReminders.length === 0 ? (
                <EmptyState 
                  type="email" 
                  onAdd={() => handleCreateNew('email')} 
                />
              ) : (
                <div className="space-y-4">
                  {emailReminders.map((reminder) => (
                    <LibraryItemCard
                      key={reminder.id}
                      item={reminder}
                      type="email"
                      isExpanded={expandedReminder === reminder.id}
                      onToggle={() => setExpandedReminder(
                        expandedReminder === reminder.id ? null : reminder.id
                      )}
                      onEdit={() => handleEditReminder(reminder)}
                      onDelete={() => {
                        setReminderToDelete(reminder);
                        setDeleteReminderModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </section>
            
            {/* Push Reminders */}
            <section className="mb-6">
              <SectionHeader 
                icon={BellIcon} 
                title="Push Notifications" 
                count={pushReminders.length} 
                isLoading={loading.pushReminders} 
              />
              
              {loading.pushReminders ? (
                <LoadingState />
              ) : error.pushReminders ? (
                <ErrorState 
                  message={error.pushReminders} 
                  onRetry={fetchGlobalPushReminders} 
                />
              ) : pushReminders.length === 0 ? (
                <EmptyState 
                  type="push" 
                  onAdd={() => handleCreateNew('push_notification')} 
                />
              ) : (
                <div className="space-y-4">
                  {pushReminders.map((reminder) => (
                    <LibraryItemCard
                      key={reminder.id}
                      item={reminder}
                      type="push"
                      isExpanded={expandedReminder === reminder.id}
                      onToggle={() => setExpandedReminder(
                        expandedReminder === reminder.id ? null : reminder.id
                      )}
                      onEdit={() => handleEditReminder(reminder)}
                      onDelete={() => {
                        setReminderToDelete(reminder);
                        setDeleteReminderModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Library Builder Modal */}
        {modalOpen && (
            <LibraryBuilderModal
              isOpen={modalOpen}
              onClose={() => {
                setModalOpen(false);
                setEditingSurvey(null);
                setEditingReminder(null);
                setSelectedType(null);
              }}
              type={selectedType}
              globalLibrary={true}  // This is important to specify that we're working with global library
              editingSurvey={editingSurvey}
              editingReminder={editingReminder}
              onLibraryUpdated={handleLibraryUpdated}
            />
          )}

        {/* Delete Survey Confirmation Modal */}
        {deleteModalOpen && surveyToDelete && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/20 w-full max-w-md overflow-hidden"
              >
                <div className="p-4 bg-red-50 flex items-center border-b border-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-200 mb-6">
                    Are you sure you want to delete the {surveyToDelete.survey_type === 'missing_form' ? 'missing form' : 'standard survey'} 
                    {' '}<span className="font-medium">"{surveyToDelete.name}"</span>?
                    <br /><br />
                    This action cannot be undone.
                  </p>
                  
                  {deleteErrorMessage && (
                    <div className="bg-red-50 rounded-xl p-4 mb-6">
                      <p className="text-red-600 font-medium">{deleteErrorMessage}</p>
                      <button
                        onClick={() => setForceDeleteMode(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                      >
                        Force Delete
                      </button>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setDeleteModalOpen(false);
                        setSurveyToDelete(null);
                      }}
                      className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteSurvey(surveyToDelete.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Delete Reminder Confirmation Modal */}
        {deleteReminderModalOpen && reminderToDelete && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/20 w-full max-w-md overflow-hidden"
              >
                <div className="p-4 bg-red-50 flex items-center border-b border-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-200 mb-6">
                    Are you sure you want to delete the {reminderToDelete.channel === 'email' ? 'email reminder' : 'push notification'}
                    {' '}<span className="font-medium">"{reminderToDelete.name}"</span>?
                    <br /><br />
                    This action cannot be undone.
                  </p>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setDeleteReminderModalOpen(false);
                        setReminderToDelete(null);
                      }}
                      className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteReminder(reminderToDelete.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm dark:shadow-gray-900/20"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default GlobalLibraryDashboard;